import * as THREE from 'three';
import { AUDIO, GAU8, RWR, VWS, GAME_HUD } from '../core/SimConfig.js';
import { CameraMode, isFlightMode } from '../camera/CameraManager.js';
import { WeaponMode } from '../weapons/WeaponSystem.js';
import {
  LOOP_EXT, LOOP_CPT, ONESHOT_EXT, ONESHOT_CPT,
  MECH_SOUNDS, GUN_SOUNDS, WEAPON_ONESHOTS, WEAPON_LOOPS,
  RWR_SOUNDS, RWR_LOOP_NAME, VWS_SOUNDS, VWS_STALL_LOOP_NAME, VWS_MASTER_LOOP_NAME, AMBIENT_LOOP_NAME,
  DISTANT_JETS_NAME, ENEMY_LOOPS,
} from './soundManifest.js';

const AIM9_LAUNCH_VOL = 0.5;
const AIM9_LOCK_VOL = 0.5;//0.075;
const AIM9_GROWL_VOL = 0.5;//0.05;

// ── Ambient bed ──────────────────────────────────────────────────────────────
// A continuous outdoor loop, independent of the engine. Its volume rests at a
// base level (dead-calm air) and swells toward base+wind as the environmental
// wind speed approaches AMBIENT_WIND_REF, then is ducked inside the cockpit
// where the canopy muffles outside ambience.
const AMBIENT_BASE_VOL = 0.5;     // volume in dead-calm air
const AMBIENT_WIND_VOL = 0.5;     // extra volume added at/above AMBIENT_WIND_REF
const AMBIENT_WIND_REF = 10;       // m/s wind speed mapped to full wind volume
const AMBIENT_COCKPIT_MUL = 0.4;   // volume multiplier when fully in cockpit view

// ── Radar Warning Receiver ───────────────────────────────────────────────────
// Contact, blip, and launch are short one-shots played polyphonically through
// AudioBufferSourceNodes (multiple can overlap during a sweep). The tracking
// loop is a single THREE.Audio loop with an exponential fade envelope.

// ── Explosions ───────────────────────────────────────────────────────────────
// Polyphonic, propagation-delayed, distance-attenuated one-shots. Each
// trigger snapshots the camera-to-source distance, schedules playback after
// `dist / speedOfSound` seconds, and routes through a fresh
// AudioBufferSourceNode + GainNode so multiple instances can overlap.
const EXPLOSION_CFG = AUDIO.explosions;

// Front / back directional groups (external only)
const FRONT_GROUP = new Set([
  'Idle_Front', 'Distant_Front', 'Maximum_Front',
  'Startup_Front', 'Shutdown_Front',
]);

const BACK_GROUP = new Set([
  'Idle_Back', 'Distant_Back',
  'Startup_Back', 'Shutdown_Back',
]);

// ── Gain / Pitch formulas from sound-library docs ────────────────────────────
// RPM = normalized 0–1, SPEED = m/s

function idleGain(rpm) {
  return rpm >= 0.5 ? 1 - (2 * rpm - 1) ** 2 : 1;
}
function idlePitch(rpm) {
  return 2.17 * rpm + 1;
}

function idleCockpitGain(rpm) {
  return 1 - rpm ** 2;
}

function idleBackPitch(rpm) {
  return 1.5 * rpm ** 2 + 0.25;
}

function maxTurbineGain(rpm) {
  return rpm < 0.258
    ? 6 * rpm ** 2
    : 0.63 * rpm ** 2 + 0.0104 * rpm + 0.36;
}
function maxTurbinePitch(rpm) {
  return 0.69 * rpm + 0.31;
}

function maxFanGain(rpm) {
  return Math.max(0, rpm - 0.5) * 2;
}
function maxFanPitch(rpm) {
  return 0.44 * rpm + 0.56;
}

function distantGain(rpm, speed) {
  return (0.7 * rpm + 0.3) * Math.min(1, speed / 30);
}

function distantFBGain(rpm, speed) {
  return rpm ** 2 * Math.min(1, speed / 30);
}

function flybyGain(rpm, speed) {
  return rpm * Math.min(1, speed / 250);
}

function maxFrontGain(rpm, speed) {
  return rpm * Math.min(1, speed / 30);
}

function windCockpitGain(speed) {
  return Math.min(1, speed / 250);
}
function windCockpitPitch(speed) {
  return 0.7 + 0.3 * Math.min(1, speed / 250);
}

// Per-sound formula dispatch — returns { gain, pitch }
function formulaFor(name, rpm, speed, pitchRate, onGround, buffet) {
  switch (name) {
    // ── External ──────────────────────
    case 'Idle':
    case 'Idle_Front':
      return { gain: idleGain(rpm), pitch: idlePitch(rpm) };

    case 'Idle_Back':
      return { gain: 1, pitch: idleBackPitch(rpm) };

    case 'Maximum_Turbine':
      return { gain: maxTurbineGain(rpm), pitch: maxTurbinePitch(rpm) };

    case 'Maximum_Fan':
      return { gain: maxFanGain(rpm), pitch: maxFanPitch(rpm) };

    case 'Maximum':
      return { gain: maxTurbineGain(rpm), pitch: 1 };

    case 'Maximum_Front':
      return { gain: maxFrontGain(rpm, speed), pitch: 1 };

    case 'Distant':
      return { gain: distantGain(rpm, speed), pitch: 1 };

    case 'Distant_Front':
    case 'Distant_Back':
      return { gain: distantFBGain(rpm, speed), pitch: 1 };

    case 'Flyby':
      return { gain: flybyGain(rpm, speed), pitch: 1 };

    // ── Cockpit ───────────────────────
    case 'Idle_cockpit':
      return { gain: idleCockpitGain(rpm), pitch: idlePitch(rpm) };

    case 'Maximum_cockpit':
      return { gain: maxTurbineGain(rpm), pitch: maxTurbinePitch(rpm) };

    case 'Avionics_cockpit':
      return { gain: 1, pitch: 1 };

    case 'Wind_cockpit':
      return { gain: windCockpitGain(speed), pitch: windCockpitPitch(speed) };

    case 'Turbulence_cockpit':
      return { gain: Math.min(1, Math.abs(pitchRate) / AUDIO.turbulence.maxPitchRate), pitch: 1 };

    case 'Ground_cockpit': {
      // Doubles as the airframe buffet rumble: the ground roll drives it on the
      // runway, high-AoA buffet in the air (the two are mutually exclusive).
      const g = onGround
        ? Math.min(1.25, speed / AUDIO.groundRumble.speedMax)
        : buffet * AUDIO.buffetRumble.gain;
      return { gain: g, pitch: 0.8 + 0.4 * g };
    }

    default:
      return { gain: 1, pitch: 1 };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// AudioManager
// ─────────────────────────────────────────────────────────────────────────────

export default class AudioManager {
  constructor({ camera, aircraft, cameraManager, weaponSystem, enemyMissileSystem = null, assets }) {
    this.assets = assets;
    this.aircraft = aircraft;
    this.cameraManager = cameraManager;
    this.weaponSystem = weaponSystem;
    this.enemyMissileSystem = enemyMissileSystem;
    this.enemyManager = null;        // set late via setEnemyManager()
    this._camera = camera;

    // Three.js audio plumbing
    this.listener = new THREE.AudioListener();
    camera.add(this.listener);

    // Pause state — see setPaused(). Suspends the whole AudioContext so no world
    // voice is processed while the sim is frozen.
    this._paused = false;
    this._ctxRequest = null;  // 'running' | 'suspended' while a state change is in flight

    // Resume AudioContext on first user gesture (browser autoplay policy)
    this._contextResumed = false;
    const ctx = this.listener.context;
    if (ctx.state === 'running') {
      this._contextResumed = true;
    } else {
      const resume = () => {
        // Re-sync rather than leaving it running: the gesture that satisfies the
        // autoplay policy can arrive while the sim is paused.
        ctx.resume().then(() => { this._contextResumed = true; this._syncContext(); });
        document.removeEventListener('click', resume);
        document.removeEventListener('keydown', resume);
      };
      document.addEventListener('click', resume);
      document.addEventListener('keydown', resume);
      // The gesture may already be behind us: the first-run intro builds this
      // manager after the loading screen's ENTER click, and nothing else is
      // clicked until the player takes the stick. Sticky activation lets the
      // context resume now; where it can't, the listeners above still will.
      if (navigator.userActivation?.hasBeenActive) resume();
    }

    this._sounds = {};        // name → THREE.Audio
    this._ready = false;      // true once all buffers are bound
    this._started = false;    // true once looping sounds are playing

    // Camera blend: 0 = full external, 1 = full cockpit
    this._cockpitBlend = cameraManager.cockpitView ? 1 : 0;

    // Track engine state transitions for one-shot triggers
    this._prevEngineStatus = aircraft.engineState.status;

    // Mechanical sound state (cockpit-only: flaps, gear)
    this._prevFlapStage = aircraft.flapStage;
    this._gearAnimating = false;
    this._gearLoopFade = 0; // 0 = silent, 1 = full volume

    // Gun sound state
    this._gunFiring = false; // was the gun loop playing last frame

    // AIM-9 seeker tone state (growl = searching, lock = locked)
    this._aim9Locked = false;
    this._aim9Growling = false;

    // Loop fade envelope: 0 = silent, 1 = full formula-driven volume
    // Ramps up after startup completes, ramps down when shutdown begins
    this._loopFade = aircraft.engineState.isRunning ? 1 : 0;

    // One-shot fade envelope: used to fade one-shots out/in at boundaries
    this._oneshotFade = 1;

    // Master fade envelope: a single global multiplier over every voice (loops,
    // one-shots, explosions, RWR) applied at the listener's master gain. Driven
    // by setMasterFade() at start-screen ↔ scenario boundaries so audio ramps
    // with the camera glide / screen fade instead of popping in. 1 = full volume.
    //
    // Born SILENT: every entry path (start-screen preview, scenario launch,
    // restart) explicitly fades this in. Starting at 0 guarantees no voice — in
    // particular the ambient bed, which starts itself on the first live frame —
    // can sound at full volume before its fade-in has been requested. (The first
    // user gesture that resumes the AudioContext is often the "enter scenario"
    // click, so a non-zero birth value would pop exactly as you enter.)
    this._masterFade = 0;
    this._masterFadeTarget = 0;
    this._masterFadeRate = 0; // per-second; 0 = snap (set by setMasterFade)

    // User master volume (0..1) from the settings overlay. Independent of the
    // scenario fade above — the two multiply together at the listener gain, so
    // the user level scales everything while the fade still glides in/out.
    this._masterVolume = 1;
    this.listener.setMasterVolume(this._masterFade * this._masterVolume);

    // Directional angle cache
    this._nAngle = 0.5;

    // Proximity loops (distant jets + the per-enemy-type loops) — smoothed
    // summed gain per voice, keyed by sound name. See _driveProximityLoop.
    this._proximityGains = {};

    // Scratch accumulator for the per-enemy-type proximity sums, keyed by
    // AUDIO.enemyLoops key. Allocated once and zeroed each frame.
    this._enemyProx = {};
    for (const key of Object.keys(AUDIO.enemyLoops)) this._enemyProx[key] = 0;

    // Temp vectors
    this._vFrom = new THREE.Vector3();
    this._vTo = new THREE.Vector3();

    // Explosion voices: AudioBuffer cache + scheduled playback queue
    this._explosionBuffers = {};   // name → AudioBuffer
    this._pendingExplosions = [];  // [{ buffer, volume, fireAt }]

    // RWR state
    this._rwrBuffers = {};           // name → AudioBuffer (Contact/Blip/Launch)
    this._rwrContacts = null;        // Set of entities that were in radar range last tick (null = uninitialised; first audible frame seeds without firing)
    this._rwrBlipTimer = RWR.blipInterval; // s until next sweep
    this._rwrPendingBlips = [];      // [fireAt] in AudioContext time
    this._rwrTrackingFade = 0;       // 0..1 envelope on the tracking loop
    this._rwrAudible = false;        // gated by camera mode + context state
    this._rwrAlertSource = null;     // shared voice for Contact + Launch one-shots, so a fresh alert cuts off the previous (Blips stay polyphonic)

    // Voice Warning System state (GPWS-style spoken cautions)
    this._vwsBuffers = {};           // name → AudioBuffer (Altitude/Pullup)
    this._vwsAltArmed = true;        // false after an altitude callout until we climb clear of the floor
    this._vwsPullupTimer = 0;        // s until the pull-up callout may repeat (0 = ready)
    this._vwsAltSource = null;       // active "Altitude" buffer source, so "Pull up" can cut it off
    this._vwsPullupSource = null;    // active "Pull up" buffer source, so a fresh callout won't retrigger over itself
    this._vwsStallFade = 0;          // 0..1 envelope on the stall buffet loop
    this._vwsStalling = false;       // hysteretic stall state (drives the loop on/off)
    this._masterCautionFade = 0;     // 0..1 envelope on the master-caution loop (engine damage)

    // Set once the A-10 is destroyed: stops every A-10 voice and gates update()
    // so nothing restarts, while still letting queued explosion blasts play.
    this._silenced = false;

    this._bindBuffers();
  }

  setEnemyManager(em) { this.enemyManager = em; }

  // ── Total destruction ──────────────────────────────────────────────────────
  // Cut every A-10-originated voice (engine, gun, mechanical, RWR, VWS,
  // master-caution, ambient) and latch update() into a silenced state so none of
  // them restart. Pending/scheduled explosion voices are intentionally left
  // alone — they route through the listener gain, not these voices, so the crash
  // blast still rings out.
  silence() {
    this._silenced = true;
    for (const s of Object.values(this._sounds)) {
      if (s && s.isPlaying) s.stop();
    }
    this._started = false;
    this._rwrPendingBlips.length = 0;
    this._rwrTrackingFade = 0;
    this._vwsStallFade = 0;
    this._masterCautionFade = 0;
    this._proximityGains = {};
  }

  // ── Master fade ──────────────────────────────────────────────────────────────
  // Ramp the global master volume toward `target` (0..1) over `duration` seconds.
  // Multiplies every voice at the listener's master gain, so it's independent of
  // the per-sound engine/event envelopes. duration <= 0 snaps immediately.
  // Stepped each frame in update(); when the AudioContext isn't yet running the
  // ramp simply waits (nothing is audible anyway) and resumes once it goes live.
  setMasterFade(target, duration = 0) {
    this._masterFadeTarget = Math.max(0, Math.min(1, target));
    if (duration > 0) {
      this._masterFadeRate = 1 / duration;
    } else {
      this._masterFade = this._masterFadeTarget;
      this._masterFadeRate = 0;
      this.listener.setMasterVolume(this._masterFade * this._masterVolume);
    }
  }

  // ── User master volume ─────────────────────────────────────────────────────
  // Set the user's master volume (0..1) from the settings overlay. Re-applies
  // the listener gain immediately, composed with the current scenario fade.
  setMasterVolume(v) {
    this._masterVolume = Math.max(0, Math.min(1, v));
    this.listener.setMasterVolume(this._masterFade * this._masterVolume);
  }

  // ── Pause ──────────────────────────────────────────────────────────────────
  // Freeze/unfreeze the whole scenario soundscape with the sim. This suspends
  // the AudioContext rather than muting it: muting would leave every loop, panner
  // and gain node being processed on the audio thread for a screen where nothing
  // is audible, whereas a suspended context does no work at all. It also freezes
  // context time, so looping voices and scheduled one-shots (explosions, RWR
  // blips) pick up exactly where they left off instead of skipping ahead by the
  // length of the pause.
  //
  // Only Web Audio is affected, which is exactly the in-world set — menu clicks
  // (ui/uiSound.js), the music player and instructor voicelines are
  // HTMLAudioElements and keep working over the pause menu.
  //
  // Idempotent and cheap (a state compare), so App can call it every frame.
  setPaused(paused) {
    this._paused = !!paused;
    this._syncContext();
  }

  // Drive the AudioContext toward the state _paused implies. No-ops when it's
  // already there or the same change is already in flight; a change requested
  // while the opposite one is pending is issued immediately (the Web Audio calls
  // are ordered), so rapid pause toggling can't leave it stuck.
  _syncContext() {
    const ctx = this.listener.context;
    const want = this._paused ? 'suspended' : 'running';
    // Before the first user gesture the context is suspended by policy — leave it
    // alone and let the gesture handler resume it.
    if (want === 'running' && !this._contextResumed) return;
    if (ctx.state === want || this._ctxRequest === want) return;
    this._ctxRequest = want;
    const settle = () => { if (this._ctxRequest === want) this._ctxRequest = null; };
    const p = want === 'suspended' ? ctx.suspend() : ctx.resume();
    p.then(settle, settle);
  }

  _updateMasterFade(dt) {
    if (this._masterFade === this._masterFadeTarget) return;
    const dir = this._masterFadeTarget > this._masterFade ? 1 : -1;
    this._masterFade += dir * this._masterFadeRate * dt;
    if (dir > 0 ? this._masterFade >= this._masterFadeTarget
               : this._masterFade <= this._masterFadeTarget) {
      this._masterFade = this._masterFadeTarget;
    }
    this.listener.setMasterVolume(this._masterFade * this._masterVolume);
  }

  // ── Buffer binding ───────────────────────────────────────────────────────────
  // Buffers are decoded + cached once by AssetManager (tracked by the loading
  // progress bar). Here we just bind those cached buffers to playback voices —
  // synchronous, so the manager is ready the instant it's constructed.

  _bindBuffers() {
    const bind = (name, loop) => {
      const buffer = this.assets?.getAudioBuffer(name);
      if (!buffer) {
        console.warn(`AudioManager: missing audio buffer "${name}"`);
        return;
      }
      const audio = new THREE.Audio(this.listener);
      audio.setBuffer(buffer);
      audio.setLoop(loop);
      audio.setVolume(0);
      this._sounds[name] = audio;
    };

    for (const name of LOOP_EXT) bind(name, true);
    for (const name of LOOP_CPT) bind(name, true);
    for (const name of ONESHOT_EXT) bind(name, false);
    for (const name of ONESHOT_CPT) bind(name, false);
    for (const [name, cfg] of Object.entries(MECH_SOUNDS)) bind(name, cfg.loop);
    for (const [name, cfg] of Object.entries(GUN_SOUNDS)) bind(name, cfg.loop);
    for (const [name, cfg] of Object.entries(WEAPON_ONESHOTS)) bind(name, cfg.loop);
    for (const [name, cfg] of Object.entries(WEAPON_LOOPS)) bind(name, cfg.loop);

    // Explosion + RWR one-shots play through per-trigger AudioBufferSourceNodes
    // so multiple can overlap — they need the raw buffers, not THREE.Audio voices.
    for (const name of Object.keys(EXPLOSION_CFG.sounds)) {
      this._explosionBuffers[name] = this.assets?.getAudioBuffer(name);
    }
    for (const name of Object.keys(RWR_SOUNDS)) {
      this._rwrBuffers[name] = this.assets?.getAudioBuffer(name);
    }
    for (const name of Object.keys(VWS_SOUNDS)) {
      this._vwsBuffers[name] = this.assets?.getAudioBuffer(name);
    }

    // RWR tracking loop — single THREE.Audio voice with fade envelope.
    bind(RWR_LOOP_NAME, true);

    // VWS stall buffet loop — single THREE.Audio voice with fade envelope.
    bind(VWS_STALL_LOOP_NAME, true);

    // VWS master-caution loop — battle-damage warning, same envelope pattern.
    bind(VWS_MASTER_LOOP_NAME, true);

    // Ambient environment bed — continuous loop, bound like the RWR voice.
    bind(AMBIENT_LOOP_NAME, true);

    // Enemy proximity loops — one shared voice per enemy type, gain driven by
    // the summed proximity of every live enemy of that type (_updateEnemyLoops).
    for (const name of Object.keys(ENEMY_LOOPS)) bind(name, true);

    // Distant-jet proximity loop — a SECOND voice on the existing Distant.wav
    // buffer (so the A-10's own engine Distant layer is untouched). Bound by
    // hand rather than via bind() because it has no manifest entry of its own.
    const distantBuf = this.assets?.getAudioBuffer('Distant');
    if (distantBuf) {
      const a = new THREE.Audio(this.listener);
      a.setBuffer(distantBuf);
      a.setLoop(true);
      a.setVolume(0);
      this._sounds[DISTANT_JETS_NAME] = a;
    } else {
      console.warn('AudioManager: missing audio buffer "Distant" for distant-jet loop');
    }

    this._ready = true;
  }

  // ── Loop start / stop ──────────────────────────────────────────────────────

  _startLoops() {
    if (this._started) return;
    this._started = true;
    for (const name of [...LOOP_EXT, ...LOOP_CPT]) {
      const s = this._sounds[name];
      if (s && s.buffer && !s.isPlaying) {
        s.setVolume(0);
        s.play();
      }
    }
  }

  _stopLoops() {
    if (!this._started) return;
    this._started = false;
    for (const name of [...LOOP_EXT, ...LOOP_CPT]) {
      const s = this._sounds[name];
      if (s && s.isPlaying) s.stop();
    }
  }

  // ── One-shot triggers ──────────────────────────────────────────────────────

  _playStartup() {
    for (const name of [...ONESHOT_EXT, ...ONESHOT_CPT]) {
      if (!name.startsWith('Startup')) continue;
      const s = this._sounds[name];
      if (s && s.buffer) {
        s.setVolume(1);
        if (s.isPlaying) s.stop();
        s.play();
      }
    }
  }

  _playShutdown() {
    // Loops are faded out gradually by the crossfade envelope
    for (const name of [...ONESHOT_EXT, ...ONESHOT_CPT]) {
      if (!name.startsWith('Shutdown')) continue;
      const s = this._sounds[name];
      if (s && s.buffer) {
        s.setVolume(1);
        if (s.isPlaying) s.stop();
        s.play();
      }
    }
  }

  _stopOneshots() {
    for (const name of [...ONESHOT_EXT, ...ONESHOT_CPT]) {
      const s = this._sounds[name];
      if (s && s.isPlaying) s.stop();
    }
  }

  // ── Per-frame update ───────────────────────────────────────────────────────

  update(dt, windSpeed = 0) {
    if (!this._ready) return;
    if (this.listener.context.state !== 'running') return;

    // Global master fade (start-screen ↔ scenario boundaries) — applied first so
    // it ducks every voice updated below for the rest of this frame.
    this._updateMasterFade(dt);

    // Destroyed wreck: every A-10 voice is already stopped; only keep draining
    // scheduled explosion blasts (incl. the crash itself) so they still fire.
    if (this._silenced) {
      this._updateExplosions();
      return;
    }

    const aircraft = this.aircraft;
    const engine = aircraft.engineState;
    const cm = this.cameraManager;

    // Start loops on first frame where context is live and engine is already on
    if (!this._started && engine.isRunning && !engine.isTransitioning) {
      this._startLoops();
      this._loopFade = 1;
    }

    const fadeDuration = AUDIO.transitionFade;
    const fadeRate = 1 / Math.max(0.01, fadeDuration);

    // ── Detect engine state transitions ──────────────────────────────────
    const status = engine.status;
    if (status !== this._prevEngineStatus) {
      if (engine.isStarting) {
        this._playStartup();
        this._loopFade = 0;
        this._oneshotFade = 1;
      } else if (engine.isStopping) {
        // Start shutdown one-shots and begin fading loops out
        this._playShutdown();
        this._oneshotFade = 0; // fade in the shutdown sound
      } else if (engine.isRunning) {
        // Startup just completed — one-shots will fade out naturally
        // Loops are already started (see below), just ensure fade completes
        this._stopOneshots();
      } else {
        // Engine just turned off → stop everything
        this._stopOneshots();
        this._stopLoops();
        this._loopFade = 0;
      }
      this._prevEngineStatus = status;
    }

    // ── Crossfade envelope updates ───────────────────────────────────────
    if (engine.isStarting) {
      // Start loops early and fade them in during the tail of the startup sound
      const progress = engine.transitionProgress;
      const fadeStart = 1 - (fadeDuration / AUDIO.startup.duration);
      if (progress >= fadeStart) {
        if (!this._started) this._startLoops();
        this._loopFade = Math.min(1, (progress - fadeStart) / (1 - fadeStart));
        // Fade out startup one-shots as loops come in
        this._oneshotFade = 1 - this._loopFade;
      }
    } else if (engine.isStopping) {
      // Fade loops out, fade shutdown one-shot in
      this._loopFade = Math.max(0, this._loopFade - fadeRate * dt);
      this._oneshotFade = Math.min(1, this._oneshotFade + fadeRate * dt);
      // Stop loops once fully faded
      if (this._loopFade <= 0 && this._started) this._stopLoops();
    } else if (engine.isRunning) {
      // Ensure fade is at 1 (handles residual ramp after startup completes)
      this._loopFade = Math.min(1, this._loopFade + fadeRate * dt);
      this._oneshotFade = Math.max(0, this._oneshotFade - fadeRate * dt);
    }

    // ── Camera cockpit blend ─────────────────────────────────────────────
    // `cockpitView` resolves to the view we're heading INTO during a transition,
    // not the one we're still leaving (which only flips at the very end of the
    // glide). Keying off the latter would hold the blend at the old value for
    // the whole transition and then snap it in a single frame when the camera
    // arrives — an audible click as the cockpit/external engine loops jump. It
    // also covers the action-camera mounts inside the canopy, which get the
    // interior mix for the same reason the cockpit view does.
    const targetBlend = cm.cockpitView ? 1 : 0;
    if (cm._transitioning) {
      // Smooth blend during camera transition
      const rate = 1 / Math.max(0.01, AUDIO.crossfadeDuration);
      const dir = targetBlend > this._cockpitBlend ? 1 : -1;
      this._cockpitBlend += dir * rate * dt;
      this._cockpitBlend = Math.max(0, Math.min(1, this._cockpitBlend));
    } else {
      this._cockpitBlend = targetBlend;
    }
    const extBlend = 1 - this._cockpitBlend;
    const cptBlend = this._cockpitBlend;

    // ── Ambient environment bed (wind-driven, camera-attenuated) ─────────
    this._updateAmbient(cptBlend, windSpeed);

    // ── Distant-jet proximity loop (other jets + far A-10 wooshing past) ─
    this._updateDistantJets(dt, cptBlend);

    // ── Per-enemy-type proximity loops (rotors, driving, launcher idle) ──
    this._updateEnemyLoops(dt, cptBlend);

    // ── Mechanical cockpit sounds (flaps, gear) ──────────────────────────
    this._updateMechanical(dt, cptBlend);

    // ── Gun sounds ──────────────────────────────────────────────────────
    this._updateGunSound(cptBlend);

    // ── Missile launch one-shots ────────────────────────────────────────
    this._updateMissileLaunchSound();

    // ── AIM-9 lock tone (loops while a target is locked) ────────────────
    this._updateAIM9LockSound();

    // ── Radar Warning Receiver ──────────────────────────────────────────
    this._updateRWR(dt);

    // ── Voice Warning System (altitude / pull-up callouts) ──────────────
    this._updateVWS(dt);

    // ── Master caution (engine battle damage) ───────────────────────────
    this._updateMasterCaution(dt);

    // ── Explosions: drain weapon detonations + fire scheduled voices ────
    this._drainWeaponExplosions();
    this._updateExplosions();

    // ── Directional angle + camera distance (external camera only) ───────
    // n_angle: 1 = looking from front, 0 = looking from behind
    const fwd = aircraft.forwardVector;
    this._vTo.copy(aircraft.position).sub(cm.camera.position);
    const camDist = this._vTo.length();
    this._vTo.normalize();
    const dot = fwd.dot(this._vTo);
    this._nAngle = 0.5 * Math.abs(dot - 1); // remap [-1,1] → [1,0] → abs → [0,1]

    // Distance attenuation for the external engine loops/one-shots. The chase and
    // start-orbit cameras sit within refDistance (full volume); the enemy-orbit
    // camera can sit far away, where the loops roll off to silence.
    // Cockpit loops are exempt (passed a distGain of 1 below).
    const distGain = this._engineDistanceGain(camDist);

    const nAngle = this._nAngle;
    const frontGain = 0.82 * nAngle + 0.18;
    const backGain = nAngle * nAngle - 2 * nAngle + 1;

    // ── Audio parameters ─────────────────────────────────────────────────
    const rpm = aircraft.normalizedRPM;
    const speed = aircraft.airspeed;
    const pitchRate = aircraft.angularVelocity.x; // body-local pitch rate approx
    const onGround = aircraft.onGround;
    const buffet = aircraft.buffetIntensity;

    // ── Update looping sounds ────────────────────────────────────────────
    if (this._started) {
      for (const name of LOOP_EXT) {
        this._applyFormula(name, rpm, speed, pitchRate, onGround, buffet, extBlend, frontGain, backGain, distGain);
      }
      for (const name of LOOP_CPT) {
        this._applyFormula(name, rpm, speed, pitchRate, onGround, buffet, cptBlend, 1, 1, 1);
      }
    }

    // ── Update one-shot volumes for camera blend + directional ───────────
    if (engine.isTransitioning) {
      for (const name of ONESHOT_EXT) {
        this._applyOneshotBlend(name, extBlend, frontGain, backGain, distGain);
      }
      for (const name of ONESHOT_CPT) {
        this._applyOneshotBlend(name, cptBlend, 1, 1, 1);
      }
    }
  }

  // Inverse-distance attenuation for the external engine voices: full volume
  // within refDistance, 1/(dist/refDistance) past it, then a linear taper to a
  // hard 0 at maxDistance so a distant view (the enemy orbit) goes fully silent
  // instead of leaving an inaudible tail.
  _engineDistanceGain(dist) {
    const { refDistance, maxDistance } = AUDIO.engineDistance;
    if (dist <= refDistance) return 1;
    if (dist >= maxDistance) return 0;
    const inv = refDistance / dist;
    const taper = 1 - (dist - refDistance) / (maxDistance - refDistance);
    return inv * taper;
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _applyFormula(name, rpm, speed, pitchRate, onGround, buffet, cameraBlend, frontGain, backGain, distGain = 1) {
    const s = this._sounds[name];
    if (!s || !s.isPlaying) return;

    const { gain, pitch } = formulaFor(name, rpm, speed, pitchRate, onGround, buffet);
    const cfg = AUDIO.sounds[name] || { gainMul: 1, pitchMul: 1 };

    // Directional multiplier
    let dirGain = 1;
    if (FRONT_GROUP.has(name)) dirGain = frontGain;
    else if (BACK_GROUP.has(name)) dirGain = backGain;

    const finalGain = gain * cfg.gainMul * cameraBlend * dirGain * distGain * this._loopFade;
    const finalPitch = pitch * cfg.pitchMul;

    s.setVolume(Math.max(0, finalGain));
    if (finalPitch > 0) s.setPlaybackRate(finalPitch);
  }

  // ── Ambient environment bed ────────────────────────────────────────────────
  // Continuous outdoor ambience, independent of the engine loops. Starts on the
  // first live frame and rests at AMBIENT_BASE_VOL, swelling toward
  // AMBIENT_BASE_VOL + AMBIENT_WIND_VOL as the environmental wind speed
  // approaches AMBIENT_WIND_REF, and is ducked toward AMBIENT_COCKPIT_MUL as the
  // view blends into the cockpit. The listener's master gain handles scenario
  // fades, so this only computes the wind/camera mix.
  _updateAmbient(cptBlend, windSpeed) {
    const s = this._sounds[AMBIENT_LOOP_NAME];
    if (!s || !s.buffer) return;
    if (!s.isPlaying) { s.setVolume(0); s.play(); }

    const windN = Math.min(1, Math.max(0, windSpeed) / AMBIENT_WIND_REF);
    const cockpitMul = 1 - (1 - AMBIENT_COCKPIT_MUL) * cptBlend;
    s.setVolume((AMBIENT_BASE_VOL + AMBIENT_WIND_VOL * windN) * cockpitMul);
  }

  // ── Proximity loops ──────────────────────────────────────────────────────────
  // A family of shared, NON-positional loops — one voice per sound source type,
  // never one voice per entity. Each contributor within range adds a 0..1
  // proximity term and the terms sum (capped at maxGain), so a lone helicopter
  // swells as it closes and a column of BTRs beside the camera simply pins the
  // loop at full volume. No panning, no per-entity imaging: the mix answers
  // "how much of this is near me", not "where is it".
  //
  // Two users: the distant-jet woosh (_updateDistantJets, generous gameplay
  // range) and the per-enemy-type loops (_updateEnemyLoops, short realistic
  // range). Both share _proximityTerm for the falloff and _driveProximityLoop
  // for the envelope + voice lifecycle.

  // Distance factor for one contributor: 1 inside fullDistance, 0 at/past
  // maxDistance, and a ramp between the two — linear by default, bent toward
  // silence by cfg.falloff > 1 so the loop stays quiet until you're genuinely
  // close (and a crowd can't sum its way into audibility from far off).
  _proximityTerm(cfg, pos) {
    const dx = pos.x - this._vFrom.x;
    const dy = pos.y - this._vFrom.y;
    const dz = pos.z - this._vFrom.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d >= cfg.maxDistance) return 0;
    if (d <= cfg.fullDistance) return 1;
    const t = (cfg.maxDistance - d) / Math.max(1, cfg.maxDistance - cfg.fullDistance);
    return cfg.falloff && cfg.falloff !== 1 ? Math.pow(t, cfg.falloff) : t;
  }

  // Cap + scale the summed proximity, duck it for the cockpit, smooth toward it,
  // and start/stop the voice as the envelope crosses zero (so idle types cost
  // nothing and a camera switch can't pop the loop).
  _driveProximityLoop(name, cfg, prox, dt, cptBlend) {
    const s = this._sounds[name];
    if (!s || !s.buffer) return;

    const cockpitMul = 1 - (1 - cfg.cockpitMul) * cptBlend;
    const target = Math.min(cfg.maxGain, prox) * cfg.volume * cockpitMul;
    const rate = 1 / Math.max(0.01, cfg.fadeTime);
    let gain = this._proximityGains[name] || 0;
    gain += (target - gain) * Math.min(1, rate * dt);
    this._proximityGains[name] = gain;

    const wantPlaying = gain > 0.001 || target > 0.001;
    if (wantPlaying && !s.isPlaying) { s.setVolume(0); s.play(); }
    if (s.isPlaying) {
      s.setVolume(Math.max(0, gain));
      if (!wantPlaying && gain <= 0.001) s.stop();
    }
  }

  // Distant-jet woosh. Contributors: all enemy planes (always, in every view)
  // plus the A-10 itself, but only from the detached enemy-orbit camera, where
  // its own near-field engine loops have faded out — chase and
  // cockpit hear those instead, so it would add nothing there. Slow/parked jets
  // contribute proportionally less (speedRef).
  _updateDistantJets(dt, cptBlend) {
    const cfg = AUDIO.distantJets;
    const speedRef = Math.max(1, cfg.speedRef);

    // Listener (camera) world position — read once, reused by _proximityTerm.
    this._camera.getWorldPosition(this._vFrom);

    let prox = 0;
    const entities = this.enemyManager?.entities;
    if (entities) {
      for (let i = 0; i < entities.length; i++) {
        const e = entities[i];
        if (!e || e.dead || !e.isJet) continue;
        const pos = e.flight?.position;
        if (!pos) continue;
        const near = this._proximityTerm(cfg, pos);
        if (near <= 0) continue;
        const v = e.flight.velocity;
        const speed = v ? Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) : 0;
        prox += near * Math.min(1, speed / speedRef);
      }
    }

    const cm = this.cameraManager;
    const mode = cm?._transitioning ? cm._toMode : cm?.mode;
    if (mode === CameraMode.ENEMY) {
      const near = this._proximityTerm(cfg, this.aircraft.position);
      if (near > 0) prox += near * Math.min(1, this.aircraft.airspeed / speedRef);
    }

    this._driveProximityLoop(DISTANT_JETS_NAME, cfg, prox, dt, cptBlend);
  }

  // Per-enemy-type loops (helicopter rotors, vehicle driving, launcher idle).
  // One pass over the enemy list buckets every live entity's proximity by its
  // `audioLoop` tag, then each bucket drives its own voice. Unlike the jets,
  // these use realistic ranges: they're inaudible from a few hundred feet up and
  // only come in on a low, close pass. Destroyed wrecks are silent, and a config
  // with a `speedRef` (the driving loop) scales each vehicle by its own speed so
  // a halted one drops out rather than idling along at a standstill.
  _updateEnemyLoops(dt, cptBlend) {
    const cfgs = AUDIO.enemyLoops;
    const prox = this._enemyProx;
    for (const key in prox) prox[key] = 0;

    const entities = this.enemyManager?.entities;
    if (entities) {
      this._camera.getWorldPosition(this._vFrom);
      for (let i = 0; i < entities.length; i++) {
        const e = entities[i];
        if (!e || e.dead || e.destroyed) continue;
        const cfg = cfgs[e.audioLoop];
        if (!cfg) continue;
        const pos = e.flight?.position;
        if (!pos) continue;
        let near = this._proximityTerm(cfg, pos);
        if (near <= 0) continue;
        if (cfg.speedRef) {
          const v = e.flight.velocity;
          const speed = e.flight.forwardSpeed !== undefined
            ? Math.abs(e.flight.forwardSpeed)
            : (v ? Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) : 0);
          near *= Math.min(1, speed / Math.max(0.01, cfg.speedRef));
        }
        prox[e.audioLoop] += near;
      }
    }

    for (const key in cfgs) {
      this._driveProximityLoop(cfgs[key].sound, cfgs[key], prox[key], dt, cptBlend);
    }
  }

  // ── Mechanical sounds (cockpit-only) ──────────────────────────────────

  _updateMechanical(dt, cptBlend) {
    const aircraft = this.aircraft;
    const GEAR_FADE_TIME = 0.5;
    const MECH_VOL = 0.25;

    // ── Flaps: one-shot on stage change ──────────────────────────────
    if (aircraft.flapStage !== this._prevFlapStage) {
      this._prevFlapStage = aircraft.flapStage;
      this._playMech('Flaps');
    }

    // Update flaps volume with camera blend
    const flaps = this._sounds['Flaps'];
    if (flaps && flaps.isPlaying) flaps.setVolume(MECH_VOL * cptBlend);

    // ── Gear: start / loop / end sequence ────────────────────────────
    const gearMoving = Math.abs(aircraft.gear - aircraft.gearTarget) > 0.001;

    if (gearMoving && !this._gearAnimating) {
      // Gear animation just started
      this._gearAnimating = true;
      this._playMech('Gear_Start');
      // Start the loop silent — it will fade in
      const loop = this._sounds['Gear_Loop'];
      if (loop && loop.buffer) {
        loop.setVolume(0);
        if (!loop.isPlaying) loop.play();
      }
    } else if (!gearMoving && this._gearAnimating) {
      // Gear animation just finished
      this._gearAnimating = false;
      this._playMech('Gear_End');
    }

    // Update gear oneshot volumes with camera blend
    for (const name of ['Gear_Start', 'Gear_End']) {
      const s = this._sounds[name];
      if (s && s.isPlaying) s.setVolume(MECH_VOL * cptBlend);
    }

    // Fade gear loop in/out
    if (this._gearAnimating) {
      this._gearLoopFade = Math.min(1, this._gearLoopFade + dt / GEAR_FADE_TIME);
    } else {
      this._gearLoopFade = Math.max(0, this._gearLoopFade - dt / GEAR_FADE_TIME);
    }

    const loop = this._sounds['Gear_Loop'];
    if (loop && loop.isPlaying) {
      loop.setVolume(this._gearLoopFade * MECH_VOL * cptBlend);
      if (this._gearLoopFade <= 0) loop.stop();
    }
  }

  // ── Gun sounds (cockpit + external, blended by camera) ─────────────────

  _updateGunSound(cptBlend) {
    const ws = this.weaponSystem;
    if (!ws) return;

    const extBlend = 1 - cptBlend;
    const GUN_VOL = 0.8;

    // Determine if the gun should be making sound right now
    const shouldFire = ws.firing && ws.mode === 1 /* GAU8 */ && ws.ammo > 0;

    if (shouldFire && !this._gunFiring) {
      // Start firing — begin loops
      this._gunFiring = true;
      for (const name of ['Gun_Loop', 'Gun_Loop_External']) {
        const s = this._sounds[name];
        if (s && s.buffer) {
          s.setVolume(0);
          if (s.isPlaying) s.stop();
          s.play();
        }
      }
    } else if (!shouldFire && this._gunFiring) {
      // Stop firing — stop loops, play end sounds
      this._gunFiring = false;
      for (const name of ['Gun_Loop', 'Gun_Loop_External']) {
        const s = this._sounds[name];
        if (s && s.isPlaying) s.stop();
      }
      // Play end sounds
      for (const name of ['Gun_End', 'Gun_End_External']) {
        const s = this._sounds[name];
        if (s && s.buffer) {
          if (s.isPlaying) s.stop();
          s.play();
        }
      }
    }

    // Update volumes based on camera blend; pitch loops by current fire rate
    const loopPitch = ws.currentFireRate / GAU8.fireRateMax;

    const loopCpt = this._sounds['Gun_Loop'];
    if (loopCpt && loopCpt.isPlaying) {
      loopCpt.setVolume(GUN_VOL * cptBlend);
      loopCpt.setPlaybackRate(loopPitch);
    }

    const loopExt = this._sounds['Gun_Loop_External'];
    if (loopExt && loopExt.isPlaying) {
      loopExt.setVolume(GUN_VOL * extBlend);
      loopExt.setPlaybackRate(loopPitch);
    }

    const endCpt = this._sounds['Gun_End'];
    if (endCpt && endCpt.isPlaying) endCpt.setVolume(GUN_VOL * cptBlend);

    const endExt = this._sounds['Gun_End_External'];
    if (endExt && endExt.isPlaying) endExt.setVolume(GUN_VOL * extBlend);
  }

  _updateMissileLaunchSound() {
    const ws = this.weaponSystem;
    if (!ws) return;
    const events = ws.drainMissileLaunches();
    if (events.length === 0) return;

    // Cockpit view gets a muffled/headset launch sample; all other views use the
    // external one. Chosen at trigger time — mid-sound camera changes aren't handled.
    const inCockpit = this.cameraManager.cockpitView;
    const s = this._sounds[inCockpit ? 'AIM9_Launch_Cockpit' : 'AIM9_Launch'];
    if (!s || !s.buffer) return;
    if (s.isPlaying) s.stop();
    s.setVolume(AIM9_LAUNCH_VOL);
    s.play();
  }

  _updateAIM9LockSound() {
    const ws = this.weaponSystem;
    if (!ws) return;

    // Both tones require the AIM-9 to be selected with a missile still on the
    // rail. Growl = seeker searching (no lock); Lock = target acquired. The two
    // are mutually exclusive, so exactly one (or neither) loops at a time.
    const aim9Ready = ws.mode === WeaponMode.AIM9 && ws.aim9MissilesRemaining > 0;
    const locked = aim9Ready && !!ws.trackedTarget;
    const growling = aim9Ready && !ws.trackedTarget;

    const lock = this._sounds['AIM9_Lock'];
    if (lock && lock.buffer) {
      if (locked && !this._aim9Locked) {
        this._aim9Locked = true;
        if (lock.isPlaying) lock.stop();
        lock.setVolume(AIM9_LOCK_VOL);
        lock.play();
      } else if (!locked && this._aim9Locked) {
        this._aim9Locked = false;
        if (lock.isPlaying) lock.stop();
      }
    }

    const growl = this._sounds['AIM9_Growl'];
    if (growl && growl.buffer) {
      if (growling && !this._aim9Growling) {
        this._aim9Growling = true;
        if (growl.isPlaying) growl.stop();
        growl.setVolume(AIM9_GROWL_VOL);
        growl.play();
      } else if (!growling && this._aim9Growling) {
        this._aim9Growling = false;
        if (growl.isPlaying) growl.stop();
      }
    }
  }

  // ── Radar Warning Receiver ─────────────────────────────────────────────────
  //
  // Each tick:
  //   1. Decide whether the RWR is audible (cockpit + chase only; the
  //      enemy view silences it). When it becomes silent we drop the
  //      contact set + queued blips so re-entering view starts clean.
  //   2. Scan enemyManager.entities, classify each radar/missile-equipped
  //      live entity by whether they're inside radar / tracking range.
  //   3. New entries into radar range fire a Contact one-shot — but only
  //      after the first audible frame, so spawning inside range doesn't
  //      machine-gun contact pings.
  //   4. Every blipInterval seconds, queue one staggered Blip per radar
  //      contact in range.
  //   5. The tracking loop fades in while any missile-equipped enemy sits
  //      inside trackingRange, otherwise fades out.
  //   6. Drain enemyMissileSystem launches → Launch one-shot per event.

  _updateRWR(dt) {
    const wasAudible = this._rwrAudible;
    const audible = this._isRWRAudible();
    this._rwrAudible = audible;

    if (!audible) {
      // Silence everything cleanly and shed any cached state so the
      // receiver re-cold-starts the next time we're back in view.
      if (wasAudible) this._silenceRWR();
      // Still drain launches so the queue doesn't grow while we're out of view.
      if (this.enemyMissileSystem) this.enemyMissileSystem.drainLaunches();
      return;
    }

    const ctx = this.listener.context;
    const playerPos = this.aircraft.position;
    const radarR2 = GAME_HUD.target.maxDistance * GAME_HUD.target.maxDistance;
    const trackR2 = RWR.trackingRange * RWR.trackingRange;

    // Build the current set of in-range radar contacts + count missile threats.
    const inRange = new Set();
    let missileThreats = 0;
    const entities = this.enemyManager?.entities;
    if (entities) {
      for (let i = 0; i < entities.length; i++) {
        const e = entities[i];
        if (!e || e.dead || e.destroyed) continue;
        if (!e.radarEquipped) continue;
        const pos = e.flight?.position;
        if (!pos) continue;
        const dx = pos.x - playerPos.x;
        const dy = pos.y - playerPos.y;
        const dz = pos.z - playerPos.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        // Entities that model their own detection (range + elevation cone +
        // terrain masking, e.g. the Tunguska) expose a live `radarContact`
        // flag; everything else falls back to a plain max-range sphere.
        const contact = e.radarContact !== undefined ? e.radarContact : d2 <= radarR2;
        if (!contact) continue;
        inRange.add(e);
        if (e.missileEquipped && d2 <= trackR2) missileThreats++;
      }
    }

    // 1) Contact transitions. _rwrContacts === null on the first audible frame
    //    after a (re-)entry to a cockpit/chase view — seed with whatever is
    //    already in range so the player doesn't get spammed.
    if (this._rwrContacts === null) {
      this._rwrContacts = inRange;
    } else {
      for (const e of inRange) {
        if (!this._rwrContacts.has(e)) {
          this._playRWROneshot('RWR_Contact', RWR.contactVolume, true);
        }
      }
      this._rwrContacts = inRange;
    }

    // 2) Sweep cadence — burst N blips when the timer expires.
    this._rwrBlipTimer -= dt;
    if (this._rwrBlipTimer <= 0) {
      const n = inRange.size;
      if (n > 0) {
        const now = ctx.currentTime;
        for (let i = 0; i < n; i++) {
          this._rwrPendingBlips.push(now + i * RWR.blipStagger);
        }
      }
      this._rwrBlipTimer += RWR.blipInterval;
      // Safety: if dt was huge (tab unfocused), don't try to "catch up" multiple sweeps.
      if (this._rwrBlipTimer < 0) this._rwrBlipTimer = RWR.blipInterval;
    }

    // Fire any blips whose scheduled time has arrived.
    if (this._rwrPendingBlips.length > 0) {
      const now = ctx.currentTime;
      for (let i = this._rwrPendingBlips.length - 1; i >= 0; i--) {
        if (now >= this._rwrPendingBlips[i]) {
          this._playRWROneshot('RWR_Blip', RWR.blipVolume);
          const last = this._rwrPendingBlips.length - 1;
          if (i !== last) this._rwrPendingBlips[i] = this._rwrPendingBlips[last];
          this._rwrPendingBlips.pop();
        }
      }
    }

    // 3) Tracking loop — exponential fade toward target.
    const target = missileThreats > 0 ? 1 : 0;
    const fadeRate = 1 / Math.max(0.01, RWR.trackingFadeTime);
    this._rwrTrackingFade += (target - this._rwrTrackingFade) * Math.min(1, fadeRate * dt);
    const loop = this._sounds[RWR_LOOP_NAME];
    if (loop && loop.buffer) {
      const wantPlaying = this._rwrTrackingFade > 0.001 || target > 0;
      if (wantPlaying && !loop.isPlaying) {
        loop.setVolume(0);
        loop.play();
      }
      if (loop.isPlaying) {
        loop.setVolume(this._rwrTrackingFade * RWR.trackingVolume);
        if (!wantPlaying && this._rwrTrackingFade <= 0.001) loop.stop();
      }
    }

    // 4) Missile launches — one Launch one-shot per drained event.
    if (this.enemyMissileSystem) {
      const launches = this.enemyMissileSystem.drainLaunches();
      for (let i = 0; i < launches.length; i++) {
        this._playRWROneshot('RWR_Launch', RWR.launchVolume, true);
      }
    }
  }

  _isRWRAudible() {
    if (this.listener.context.state !== 'running') return false;
    // Avionics-gated: the RWR / VWS / master-caution are powered off the engine,
    // so they fall silent when the aircraft is off — mirrors the cockpit HUD,
    // which blanks on the same power state.
    if (!this._avionicsPowered()) return false;
    const cm = this.cameraManager;
    if (!cm) return false;
    if (cm._transitioning) return isFlightMode(cm._toMode) || isFlightMode(cm.mode);
    return isFlightMode(cm.mode);
  }

  // Avionics power state — follows engine startup/shutdown the same way the
  // cockpit HUD does, crossing on/off at the half-way point of each transition.
  _avionicsPowered() {
    const eng = this.aircraft?.engineState;
    if (!eng) return false;
    if (eng.isStarting) return eng.transitionProgress >= 0.5;
    if (eng.isStopping) return eng.transitionProgress < 0.5;
    return eng.isRunning;
  }

  // `alert` routes Contact + Launch through a single shared voice: a fresh alert
  // stops whichever one is still sounding, so they interrupt themselves and each
  // other instead of stacking. Blips pass alert=false and stay polyphonic.
  _playRWROneshot(name, volume, alert = false) {
    const buffer = this._rwrBuffers[name];
    if (!buffer) return;
    const ctx = this.listener.context;
    if (ctx.state !== 'running') return;

    if (alert && this._rwrAlertSource) {
      try { this._rwrAlertSource.stop(); } catch { /* already stopped/ended */ }
      this._rwrAlertSource = null;
    }

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(this.listener.gain);
    src.start();
    src.onended = () => {
      src.disconnect();
      gain.disconnect();
      if (this._rwrAlertSource === src) this._rwrAlertSource = null;
    };
    if (alert) this._rwrAlertSource = src;
  }

  _silenceRWR() {
    this._rwrContacts = null;        // re-seed on next audible frame
    this._rwrPendingBlips.length = 0;
    this._rwrTrackingFade = 0;
    const loop = this._sounds[RWR_LOOP_NAME];
    if (loop && loop.isPlaying) loop.stop();
  }

  // ── Voice Warning System ───────────────────────────────────────────────────
  //
  // GPWS-style spoken cautions driven entirely off the AGL the aircraft already
  // caches each frame (aircraft.altitudeAGL) plus its vertical speed — no extra
  // terrain query. Audible only in cockpit/chase views (reuses the RWR gate) and
  // suppressed with the gear down or while on the ground (normal landings / low
  // passes). Two callouts:
  //   • "Altitude" — one-shot when descending through VWS.altitudeFloor; re-arms
  //     only after climbing VWS.altitudeHysteresis back above the floor.
  //   • "Pull up"  — imminent terrain impact: below VWS.pullupCeiling and the
  //     predicted time-to-ground (AGL / sink rate) under VWS.pullupTimeToImpact.
  //     Repeats every VWS.pullupRepeat seconds while the danger persists.
  //   • Stall buffet — a continuous loop (not a spoken callout) that runs while
  //     |alpha| is past the stall angle, with hysteresis + a short fade.

  _updateVWS(dt) {
    const agl = this.aircraft?.altitudeAGL;

    if (!this._isRWRAudible()) {
      // Out of view: re-seed altitude arming from the current AGL so returning
      // to a cockpit/chase view doesn't startle-fire on a low pass we were
      // already established in, and reset the pull-up cadence so a genuine
      // danger callout fires immediately on re-entry.
      this._vwsAltArmed = !(agl < VWS.altitudeFloor); // armed unless already below the floor
      this._vwsPullupTimer = 0;
      this._setVWSStall(false, dt);
      return;
    }

    if (agl == null || !isFinite(agl)) return; // no terrain collider bound yet

    const ac = this.aircraft;
    const gearDown = (ac.gear ?? 0) > VWS.gearDownThreshold;
    const suppressed = gearDown || ac.onGround;
    const sinkRate = -ac.velocity.y;            // +ve = descending

    // ── Stall buffet loop ─────────────────────────────────────────────────
    // Hysteretic on |alpha| vs the wing's stall angle. Suppressed on the ground
    // and below stallMinSpeed — parked/taxi/ground-roll AoA is meaningless (it's
    // derived from velocity direction and goes noisy near zero speed).
    const stallAngle = ac.stallAngle || (15 * (Math.PI / 180));
    const aoaRatio = Math.abs(ac.alpha || 0) / stallAngle;
    const stallSuppressed = ac.onGround || ac.airspeed < VWS.stallMinSpeed;
    const stalling = stallSuppressed ? false
      : this._vwsStalling ? aoaRatio > VWS.stallAoAOff   // stay on until we drop clear
        : aoaRatio >= VWS.stallAoAOn;                    // turn on once past the floor
    this._setVWSStall(stalling, dt);

    // ── "Altitude" floor callout ──────────────────────────────────────────
    if (suppressed) {
      // Re-arm once we're clear above the floor so a gear-up at low level can
      // re-evaluate, but stay silent while gear is down / on the ground.
      if (agl > VWS.altitudeFloor + VWS.altitudeHysteresis) this._vwsAltArmed = true;
    } else if (this._vwsAltArmed && agl < VWS.altitudeFloor) {
      this._playVWSOneshot('VWS_Altitude', VWS.altitudeVolume);
      this._vwsAltArmed = false;
    } else if (agl > VWS.altitudeFloor + VWS.altitudeHysteresis) {
      this._vwsAltArmed = true;
    }

    // ── "Pull up" imminent-impact callout ─────────────────────────────────
    this._vwsPullupTimer -= dt;
    const danger = !suppressed
      && agl < VWS.pullupCeiling
      && sinkRate > VWS.pullupMinSinkRate
      && agl / sinkRate < VWS.pullupTimeToImpact;
    if (danger) {
      if (this._vwsPullupTimer <= 0) {
        this._playVWSOneshot('VWS_Pullup', VWS.pullupVolume);
        this._vwsPullupTimer = VWS.pullupRepeat;
      }
    } else {
      this._vwsPullupTimer = 0; // ready to fire the instant danger returns
    }
  }

  _playVWSOneshot(name, volume) {
    const buffer = this._vwsBuffers[name];
    if (!buffer) return;
    const ctx = this.listener.context;
    if (ctx.state !== 'running') return;

    if (name === 'VWS_Pullup') {
      // Don't stack pull-up callouts: if one is still speaking, let it finish
      // rather than retriggering over itself.
      if (this._vwsPullupSource) return;
      // "Pull up" is the higher-priority callout: silence any in-progress
      // "Altitude" so the urgent warning isn't muddied by the routine one.
      this._stopVWSAltitude();
    }

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(this.listener.gain);
    src.start();

    const isAlt = name === 'VWS_Altitude';
    const isPullup = name === 'VWS_Pullup';
    src.onended = () => {
      src.disconnect();
      gain.disconnect();
      if (isAlt && this._vwsAltSource === src) this._vwsAltSource = null;
      if (isPullup && this._vwsPullupSource === src) this._vwsPullupSource = null;
    };
    if (isAlt) this._vwsAltSource = src;
    if (isPullup) this._vwsPullupSource = src;
  }

  _stopVWSAltitude() {
    const src = this._vwsAltSource;
    if (!src) return;
    this._vwsAltSource = null;
    try { src.stop(); } catch { /* already stopped/ended */ }
  }

  _stopVWSPullup() {
    const src = this._vwsPullupSource;
    if (!src) return;
    this._vwsPullupSource = null;
    try { src.stop(); } catch { /* already stopped/ended */ }
  }

  // Drive the stall buffet loop toward on/off, exponentially fading its volume
  // and starting/stopping the voice as the envelope crosses near zero.
  _setVWSStall(stalling, dt) {
    this._vwsStalling = stalling;
    const target = stalling ? 1 : 0;
    const fadeRate = 1 / Math.max(0.01, VWS.stallFadeTime);
    this._vwsStallFade += (target - this._vwsStallFade) * Math.min(1, fadeRate * dt);
    const loop = this._sounds[VWS_STALL_LOOP_NAME];
    if (!loop || !loop.buffer) return;
    const wantPlaying = this._vwsStallFade > 0.001 || target > 0;
    if (wantPlaying && !loop.isPlaying) {
      loop.setVolume(0);
      loop.play();
    }
    if (loop.isPlaying) {
      loop.setVolume(this._vwsStallFade * VWS.stallVolume);
      if (!wantPlaying && this._vwsStallFade <= 0.001) loop.stop();
    }
  }

  _stopVWSStall() {
    this._vwsStalling = false;
    this._vwsStallFade = 0;
    const loop = this._sounds[VWS_STALL_LOOP_NAME];
    if (loop && loop.isPlaying) loop.stop();
  }

  // ── Explosions ─────────────────────────────────────────────────────────────
  // Public trigger: snapshot camera distance at trigger time, schedule the
  // sound to play after `dist / speedOfSound` seconds at distance-attenuated
  // volume. Multiple overlapping instances are supported.

  playExplosion(name, x, y, z) {
    const cfg = EXPLOSION_CFG.sounds[name];
    const buffer = this._explosionBuffers[name];
    if (!cfg || !buffer) return;

    const ctx = this.listener.context;
    if (ctx.state !== 'running') return;

    // Camera world position — the camera isn't always parented to the scene,
    // but its `position` is its world position when not nested in a parent.
    const cam = this._camera;
    cam.getWorldPosition(this._vFrom);
    const dx = x - this._vFrom.x;
    const dy = y - this._vFrom.y;
    const dz = z - this._vFrom.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist > EXPLOSION_CFG.maxDistance) return;

    // Inverse-distance attenuation: full volume within refDistance, then
    // 1/(dist/refDistance) past it. Capped at 1 so trigger volume is the max.
    const att = EXPLOSION_CFG.refDistance / Math.max(EXPLOSION_CFG.refDistance, dist);
    const vol = cfg.volume * att;
    if (vol < EXPLOSION_CFG.minVolume) return;

    const fireAt = ctx.currentTime + dist / EXPLOSION_CFG.speedOfSound;
    this._pendingExplosions.push({ buffer, volume: vol, fireAt });
  }

  /**
   * Muffled interior boom for an enemy missile striking the A-10. Plays only in
   * cockpit view (a non-spatial, immediate one-shot — you're sitting in it), and
   * returns true when it fired so callers can fall back to the external blast.
   */
  playInteriorExplosion(name) {
    if (!this.cameraManager?.cockpitView) return false;
    const cfg = EXPLOSION_CFG.sounds[name];
    const buffer = this._explosionBuffers[name];
    if (!cfg || !buffer) return false;
    const ctx = this.listener.context;
    if (ctx.state !== 'running') return false;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = cfg.volume;
    src.connect(gain);
    gain.connect(this.listener.gain);
    src.start();
    src.onended = () => { src.disconnect(); gain.disconnect(); };
    return true;
  }

  // Master-caution loop — runs continuously while the A-10 has a knocked-out
  // engine, gated to cockpit/chase views like the other VWS warnings and faded
  // to avoid clicks. Mirrors _setVWSStall's envelope handling.
  _updateMasterCaution(dt) {
    const damaged = !!(this.aircraft
      && (this.aircraft.engineLeftOut || this.aircraft.engineRightOut));
    const want = damaged && this._isRWRAudible();
    const target = want ? 1 : 0;
    const fadeRate = 1 / Math.max(0.01, VWS.masterCautionFadeTime);
    this._masterCautionFade += (target - this._masterCautionFade) * Math.min(1, fadeRate * dt);
    const loop = this._sounds[VWS_MASTER_LOOP_NAME];
    if (!loop || !loop.buffer) return;
    const wantPlaying = this._masterCautionFade > 0.001 || target > 0;
    if (wantPlaying && !loop.isPlaying) {
      loop.setVolume(0);
      loop.play();
    }
    if (loop.isPlaying) {
      loop.setVolume(this._masterCautionFade * VWS.masterCautionVolume);
      if (!wantPlaying && this._masterCautionFade <= 0.001) loop.stop();
    }
  }

  _stopMasterCaution() {
    this._masterCautionFade = 0;
    const loop = this._sounds[VWS_MASTER_LOOP_NAME];
    if (loop && loop.isPlaying) loop.stop();
  }

  _drainWeaponExplosions() {
    const ws = this.weaponSystem;
    if (!ws) return;
    const events = ws.drainMissileExplosions();
    for (let i = 0; i < events.length; i++) {
      const e = events[i];
      this.playExplosion(e.name, e.x, e.y, e.z);
    }
  }

  _updateExplosions() {
    if (this._pendingExplosions.length === 0) return;
    const ctx = this.listener.context;
    if (ctx.state !== 'running') return;
    const now = ctx.currentTime;

    for (let i = this._pendingExplosions.length - 1; i >= 0; i--) {
      const p = this._pendingExplosions[i];
      if (now >= p.fireAt) {
        this._spawnExplosionVoice(p.buffer, p.volume);
        // Swap-pop to avoid O(n) splice while iterating in reverse
        const last = this._pendingExplosions.length - 1;
        if (i !== last) this._pendingExplosions[i] = this._pendingExplosions[last];
        this._pendingExplosions.pop();
      }
    }
  }

  _spawnExplosionVoice(buffer, volume) {
    const ctx = this.listener.context;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    // Route through the listener's master gain so any future master-volume
    // controls apply here too.
    gain.connect(this.listener.gain);
    src.start();
    src.onended = () => {
      src.disconnect();
      gain.disconnect();
    };
  }

  _playMech(name) {
    const s = this._sounds[name];
    if (!s || !s.buffer) return;
    if (s.isPlaying) s.stop();
    s.play();
  }

  dispose() {
    // Stop all sounds
    for (const s of Object.values(this._sounds)) {
      if (s && s.isPlaying) s.stop();
    }
    this._sounds = {};
    this._ready = false;
    this._started = false;
    // Discard any explosions still in flight — voices already started will
    // continue and clean themselves up via onended.
    this._pendingExplosions.length = 0;
    // Proximity-loop envelopes — the voices themselves are already stopped above.
    this._proximityGains = {};
    // Drop our buffer references (the buffers themselves are owned + cached by
    // AssetManager, so a fresh AudioManager just re-binds them).
    this._explosionBuffers = {};
    // RWR — pending blips are timestamps; drop them and our buffer references.
    this._rwrPendingBlips.length = 0;
    this._rwrBuffers = {};
    this._rwrContacts = null;
    // VWS — stop any in-progress callout / stall loop and drop buffer references
    // (re-bound by a fresh AudioManager).
    this._stopVWSAltitude();
    this._stopVWSPullup();
    this._stopVWSStall();
    this._stopMasterCaution();
    this._vwsBuffers = {};
    // The AudioContext is a process-wide singleton shared with whatever manager
    // replaces us — don't leave it suspended because we happened to be torn down
    // from a paused scenario.
    this._paused = false;
    this._syncContext();
    // Remove listener from camera
    if (this.listener.parent) this.listener.parent.remove(this.listener);
  }

  _applyOneshotBlend(name, cameraBlend, frontGain, backGain, distGain = 1) {
    const s = this._sounds[name];
    if (!s || !s.isPlaying) return;

    const cfg = AUDIO.sounds[name] || { gainMul: 1, pitchMul: 1 };

    let dirGain = 1;
    if (FRONT_GROUP.has(name)) dirGain = frontGain;
    else if (BACK_GROUP.has(name)) dirGain = backGain;

    s.setVolume(Math.max(0, cfg.gainMul * cameraBlend * dirGain * distGain * this._oneshotFade));
  }
}
